import Keycloak from 'keycloak-js';
import logger from '@/shared/lib/logger';

// Variável de ambiente para desabilitar autenticação Keycloak
const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

// Verificar se Keycloak está configurado
const isKeycloakConfigured = (): boolean => {
    const url = import.meta.env.VITE_KEYCLOAK_URL;
    const realm = import.meta.env.VITE_KEYCLOAK_REALM;
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;
    return !!(url && realm && clientId);
};

// Configuração Keycloak a partir de variáveis de ambiente
const getKeycloakConfig = () => {
    return {
        url: import.meta.env.VITE_KEYCLOAK_URL || '',
        realm: import.meta.env.VITE_KEYCLOAK_REALM || '',
        clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || ''
    };
};

// Instância Keycloak (inicialização lazy)
let keycloak: Keycloak | null = null;

/**
 * Obtém ou cria a instância Keycloak
 */
const getKeycloakInstance = (): Keycloak => {
    if (!keycloak) {
        const config = getKeycloakConfig();
        // Criar instância apenas se configurado, caso contrário usar configuração dummy
        if (isKeycloakConfigured() || DISABLE_AUTH) {
            keycloak = new Keycloak(config);
        } else {
            // Criar com configuração dummy para evitar erros, mas não será usado
            keycloak = new Keycloak({
                url: 'http://localhost',
                realm: 'dummy',
                clientId: 'dummy'
            });
        }
    }
    return keycloak;
};

// ID do intervalo de atualização de token (para limpeza)
let tokenRefreshIntervalId: ReturnType<typeof setInterval> | null = null;

// Rastrear estado de inicialização para evitar inicialização dupla (React StrictMode)
let initializationPromise: Promise<boolean> | null = null;
let isInitialized = false;

/**
 * Detecta se estamos em um callback OAuth.
 */
const isCallback = (): boolean => {
    const url = window.location.href;
    return url.includes('state=') && url.includes('code=');
};

/**
 * Obtém opções de inicialização com base em se estamos em um callback.
 */
const getInitOptions = () => {
    const callbackDetected = isCallback();

    if (callbackDetected) {
        return {
            checkLoginIframe: false,
            pkceMethod: 'S256' as const,
            enableLogging: import.meta.env.DEV,
        };
    }

    return {
        onLoad: 'check-sso' as const,
        checkLoginIframe: false,
        pkceMethod: 'S256' as const,
        enableLogging: import.meta.env.DEV,
    };
};

/**
 * Atualiza o token de acesso se estiver prestes a expirar.
 * @param minValidity - Validade mínima em segundos antes de atualizar
 * @returns Promise<boolean> - Se o token foi atualizado
 */
export const updateToken = (minValidity: number = 30): Promise<boolean> => {
    if (DISABLE_AUTH || !isKeycloakConfigured()) {
        return Promise.resolve(false);
    }

    const kc = getKeycloakInstance();
    if (!kc.authenticated || !kc.tokenParsed) {
        return Promise.resolve(false);
    }

    const expiresIn = kc.tokenParsed.exp! - Math.ceil(Date.now() / 1000);

    if (expiresIn > minValidity) {
        return Promise.resolve(false);
    }

    return kc.updateToken(minValidity)
        .then((refreshed) => {
            if (refreshed) {
                logger.debug('Token refreshed successfully');
            }
            return refreshed;
        })
        .catch((error) => {
            logger.error('Failed to refresh token:', error);
            throw error;
        });
};

/**
 * Configura callbacks de eventos Keycloak e intervalo de atualização de token.
 * Retorna uma função de limpeza para limpar o intervalo.
 */
export const setupKeycloakCallbacks = (): (() => void) => {
    if (DISABLE_AUTH || !isKeycloakConfigured()) {
        return () => {}; // Limpeza sem operação
    }

    const kc = getKeycloakInstance();
    
    // Callback de token expirado
    kc.onTokenExpired = () => {
        logger.debug('Token expired, attempting to renew...');
        updateToken(5).catch((error) => {
            logger.error('Failed to renew expired token:', error);
        });
    };

    // Callback de sucesso de autenticação
    kc.onAuthSuccess = () => {
        logger.debug('Authentication successful');
    };

    // Callback de erro de autenticação
    kc.onAuthError = () => {
        logger.warn('Authentication error');
    };

    // Limpar qualquer intervalo existente antes de criar um novo
    if (tokenRefreshIntervalId) {
        clearInterval(tokenRefreshIntervalId);
    }

    // Intervalo de atualização de token (a cada 5 minutos)
    tokenRefreshIntervalId = setInterval(() => {
        if (kc.authenticated) {
            updateToken(120).catch(() => {
                // Tratar silenciosamente - a atualização do token será tentada novamente
            });
        }
    }, 300000);

    // Retornar função de limpeza
    return () => {
        if (tokenRefreshIntervalId) {
            clearInterval(tokenRefreshIntervalId);
            tokenRefreshIntervalId = null;
        }
    };
};

/**
 * Inicializa Keycloak.
 * @returns Promise<boolean> - Se o usuário está autenticado
 */
export const initKeycloak = (): Promise<boolean> => {
    if (DISABLE_AUTH) {
        logger.warn('Keycloak auth disabled via VITE_DISABLE_AUTH');
        return Promise.resolve(true);
    }

    if (!isKeycloakConfigured()) {
        logger.warn('Keycloak not configured, skipping initialization');
        return Promise.resolve(false);
    }

    const kc = getKeycloakInstance();

    // Se já inicializado, retornar estado de autenticação atual
    if (isInitialized) {
        logger.debug('Keycloak already initialized, returning current state');
        return Promise.resolve(kc.authenticated || false);
    }

    // Se a inicialização está em progresso, retornar a promise existente
    if (initializationPromise) {
        logger.debug('Keycloak initialization already in progress');
        return initializationPromise;
    }

    const initOptions = getInitOptions();

    initializationPromise = new Promise((resolve, reject) => {
        kc.init(initOptions)
            .then((authenticated) => {
                logger.debug('Keycloak initialized', { authenticated });
                isInitialized = true;
                setupKeycloakCallbacks();
                resolve(authenticated);
            })
            .catch((error) => {
                logger.error('Keycloak initialization failed:', error);
                initializationPromise = null; // Permitir nova tentativa em caso de falha
                reject(error);
            });
    });

    return initializationPromise;
};

/**
 * Inicia o fluxo de login.
 */
export const loginKeycloak = (): void => {
    if (DISABLE_AUTH || !isKeycloakConfigured()) {
        logger.debug('Keycloak disabled or not configured, simulating login');
        window.location.href = window.location.origin;
        return;
    }

    const kc = getKeycloakInstance();
    kc.login({
        redirectUri: window.location.origin + '/callback',
    });
};

/**
 * Inicia o fluxo de logout.
 */
export const logoutKeycloak = (): void => {
    // Limpar intervalo de atualização de token no logout
    if (tokenRefreshIntervalId) {
        clearInterval(tokenRefreshIntervalId);
        tokenRefreshIntervalId = null;
    }

    if (DISABLE_AUTH || !isKeycloakConfigured()) {
        logger.debug('Keycloak disabled or not configured, simulating logout');
        window.location.href = window.location.origin;
        return;
    }

    const kc = getKeycloakInstance();
    kc.logout({
        redirectUri: window.location.origin,
    });
};

/**
 * Obtém o token de acesso atual.
 */
export const getToken = (): string | undefined => {
    if (DISABLE_AUTH || !isKeycloakConfigured()) {
        return undefined;
    }
    const kc = getKeycloakInstance();
    return kc.token;
};

/**
 * Verifica se o usuário está autenticado.
 */
export const isAuthenticated = (): boolean => {
    if (DISABLE_AUTH) {
        return true;
    }
    if (!isKeycloakConfigured()) {
        return false;
    }
    const kc = getKeycloakInstance();
    return kc.authenticated || false;
};

/**
 * Obtém informações do usuário do token.
 */
export const getUserInfo = () => {
    if (DISABLE_AUTH) {
        return {
            sub: 'dummy_sub',
            id: 'dummy_id',
            name: 'Usuário Dummy',
            email: 'dummy@example.com',
            preferred_username: 'dummyuser',
            given_name: 'Usuário',
            family_name: 'Dummy',
        };
    }
    if (!isKeycloakConfigured()) {
        return null;
    }
    const kc = getKeycloakInstance();
    return kc.tokenParsed;
};

export default getKeycloakInstance;